import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { getCurrentAlgoliaInsightsUserToken } from 'lib/algolia'
import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { ProductTypes } from 'modules/products'
import { NavigationOpenables } from 'shared/types/navigation'
import { interleaveArrays } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'

import { searchProductsSeo } from '../analytics'
import {
  getSearchDropdownSearchResultsData,
  getSearchDropdownTrendingProductsAndFacetsData
} from '../api'
import { SEARCH_MIN_QUERY_LENGTH, SEARCH_QUERY_DEBOUNCE_TIME } from '../config'
import useSearchResultsStore from '../store/searchStore'
import {
  SearchDropdownMode,
  SearchParamsKeys,
  SearchQuerySuggestionItem,
  SearchQuerySuggestionSource
} from '../types'

interface UseSearchDropdownProps {
  isOpen: boolean
  setOpen: (name: NavigationOpenables, value: boolean) => void
}

const useSearchDropdown = ({ isOpen, setOpen }: UseSearchDropdownProps) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [productsQueryID, setProductsQueryID] = useState<string | null>(null)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [productResults, setProductResults] = useState<ProductTypes[]>([])
  const [searchQuerySuggestionResults, setSearchQuerySuggestionResults] =
    useState<SearchQuerySuggestionItem[]>([])
  const [dropdownMode, setDropdownMode] = useState<SearchDropdownMode>(
    SearchDropdownMode.initial
  )
  const [isLoading, setIsLoading] = useState(false)
  const seededUrlQueryParamRef = useRef<string | null>(null)
  const { setPrefetchedResultsData, clearPrefetchedResultsData } =
    useSearchResultsStore()
  const latestRequestIdRef = useRef(0)
  const session = useSession()
  const customer: CustomerTypes | undefined =
    extractCustomerFromClientSession(session)

  const loadSearchResults = useCallback(
    async (searchQuery: string) => {
      const requestId = ++latestRequestIdRef.current
      const userToken = await getCurrentAlgoliaInsightsUserToken(customer?.id)

      setIsLoading(true)
      const {
        products: searchProducts,
        querySuggestions: searchQuerySuggestions,
        meta: { queryID: searchQueryID = null }
      } = await getSearchDropdownSearchResultsData(searchQuery, userToken)

      if (requestId !== latestRequestIdRef.current) return

      setProductResults(searchProducts)
      setSearchQuerySuggestionResults(
        searchQuerySuggestions.map((value: string, i: number) => ({
          value,
          source: SearchQuerySuggestionSource.instantQuerySuggestion,
          queryID: searchQueryID,
          sourcePosition: i + 1
        }))
      )
      setProductsQueryID(searchQueryID)
      setPrefetchedResultsData({
        query: searchQuery,
        queryID: searchQueryID,
        productIds: searchProducts.map((result: ProductTypes) => result.id)
      })
      setDropdownMode(SearchDropdownMode.results)
      setIsLoading(false)

      searchProductsSeo({
        customer,
        searchQuery: searchQuery
      })
    },
    [setPrefetchedResultsData, customer]
  )

  const loadTrendingResults = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current

    setIsLoading(true)
    const {
      products: trendingProductsResults,
      categoryFacets: trendingCategoryFacetsResults,
      shortNameFacets: trendingShortNameFacetsResults,
      meta: {
        productsQueryID: trendingProductsQueryID = null,
        categoryFacetsQueryID: trendingCategoryFacetsQueryID = null,
        shortNameFacetsQueryID: trendingShortNameFacetsQueryID = null
      }
    } = await getSearchDropdownTrendingProductsAndFacetsData()

    if (requestId !== latestRequestIdRef.current) return

    const trendingCategoryItems: SearchQuerySuggestionItem[] =
      trendingCategoryFacetsResults.map((value: string, i: number) => ({
        value,
        source: SearchQuerySuggestionSource.trendingCategoryFacet,
        queryID: trendingCategoryFacetsQueryID,
        sourcePosition: i + 1
      }))

    const trendingShortNameItems: SearchQuerySuggestionItem[] =
      trendingShortNameFacetsResults.map((value: string, i: number) => ({
        value,
        source: SearchQuerySuggestionSource.trendingShortNameFacet,
        queryID: trendingShortNameFacetsQueryID,
        sourcePosition: i + 1
      }))

    setProductResults(trendingProductsResults)

    // Compute query suggestions list from combined category and short name (brands) facets.
    setSearchQuerySuggestionResults(
      interleaveArrays(trendingCategoryItems, trendingShortNameItems)
    )
    setProductsQueryID(trendingProductsQueryID)
    clearPrefetchedResultsData()
    setDropdownMode(SearchDropdownMode.trending)
    setIsLoading(false)
  }, [clearPrefetchedResultsData])

  // Seed query from URL on page visit and browser history navigation
  useEffect(() => {
    const urlQueryParam = (
      searchParams?.get(SearchParamsKeys.searchQuery) ?? ''
    ).trim()

    if (!urlQueryParam) {
      seededUrlQueryParamRef.current = null
      setQuery('')

      return
    }

    if (urlQueryParam === seededUrlQueryParamRef.current) return

    seededUrlQueryParamRef.current = urlQueryParam
    setQuery(urlQueryParam)
  }, [searchParams])

  // Debounce query for algolia calls
  useEffect(() => {
    const nextQuery = query.trim()
    const timeoutId = setTimeout(
      () => setDebouncedQuery(nextQuery),
      SEARCH_QUERY_DEBOUNCE_TIME
    )

    return () => clearTimeout(timeoutId)
  }, [query])

  // Set search results or trending results for debounced query
  useEffect(() => {
    if (!isOpen) return

    // Load instant results after reaching required query length
    if (debouncedQuery.length >= SEARCH_MIN_QUERY_LENGTH) {
      void loadSearchResults(debouncedQuery)

      return
    }

    // Load trending on initial call or once the query length gets too short.
    if (
      dropdownMode === SearchDropdownMode.initial ||
      dropdownMode === SearchDropdownMode.results
    ) {
      void loadTrendingResults()

      return
    }

    // Skip instant results loading if query is too short.
    setIsLoading(false)

    // dropdownMode is intentionally excluded from the dependency array to avoid re-running issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, isOpen])

  const handleOpen = useCallback(
    () => setOpen(NavigationOpenables.searchDropdown, true),
    [setOpen]
  )
  const handleClose = useCallback(
    () => setOpen(NavigationOpenables.searchDropdown, false),
    [setOpen]
  )

  const handleSearchSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault()
      router.push(
        `${PATHS.searchResults}?${SearchParamsKeys.searchQuery}=${encodeURIComponent(query)}`
      )
      handleClose()
    },
    [query, router, handleClose]
  )

  return {
    query,
    productsQueryID,
    customerId: customer?.id,
    setQuery,
    dropdownMode,
    productResults,
    searchQuerySuggestionResults,
    isLoading,
    isOpen,
    handleOpen,
    handleClose,
    handleSearchSubmit
  }
}

export default useSearchDropdown
